/**
 * CMS block registry — declarative map of type → editor/renderer.
 *
 * To add a new block type in the future:
 *   1. Append an entry to BLOCKS with { label, defaults, Editor, Renderer }.
 *   2. (Optionally) add it to backend BLOCK_TYPES for discoverability.
 * No other changes required — the CMS Builder auto-generates the toolbar
 * button and the props form.
 */
import React from "react";
import DOMPurify from "dompurify";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash } from "@phosphor-icons/react";

/* ---------------- Reusable prop editors ---------------- */
function TextField({ label, value, onChange, placeholder, textarea }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      {textarea ? (
        <Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
}

function ListEditor({ label, items, onChange, fields, newItem }) {
  const upd = (idx, patch) => {
    const next = items.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const add = () => onChange([...(items || []), newItem()]);
  const del = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
        <Button size="sm" variant="outline" onClick={add} data-testid={`list-add-${label.toLowerCase().replace(/\s/g, "-")}`}>
          <Plus size={12} className="mr-1" />Add
        </Button>
      </div>
      <div className="space-y-3">
        {(items || []).map((it, idx) => (
          <div key={idx} className="p-3 border border-border rounded-md space-y-2 bg-secondary/20">
            <div className="flex justify-end">
              <button onClick={() => del(idx)} className="text-muted-foreground hover:text-destructive p-1" title="Remove">
                <Trash size={12} />
              </button>
            </div>
            {fields.map((f) => (
              <TextField key={f.key} label={f.label} textarea={f.textarea}
                value={it[f.key]} onChange={(v) => upd(idx, { [f.key]: v })}
                placeholder={f.placeholder} />
            ))}
          </div>
        ))}
        {(!items || items.length === 0) && (
          <div className="text-xs text-muted-foreground italic py-2">No items yet — click Add.</div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Block renderers (preview) ---------------- */
const Section = ({ children, tone = "default", className = "" }) => {
  const bg = tone === "primary" ? "bg-primary text-primary-foreground"
    : tone === "muted" ? "bg-secondary/50" : "bg-background";
  return <div className={`px-6 py-10 rounded-lg ${bg} ${className}`}>{children}</div>;
};

const HeroRender = ({ props: p }) => (
  <Section tone="muted">
    {p.eyebrow && <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{p.eyebrow}</div>}
    <h1 className="font-heading text-3xl md:text-4xl font-bold leading-tight">{p.heading || "Hero heading"}</h1>
    {p.subheading && <p className="text-sm text-muted-foreground mt-3 max-w-xl">{p.subheading}</p>}
    {p.cta_label && (
      <a href={p.cta_url || "#"} className="inline-block mt-5 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium">
        {p.cta_label}
      </a>
    )}
  </Section>
);

const FeaturesRender = ({ props: p }) => (
  <Section>
    {p.heading && <h2 className="font-heading text-2xl font-bold mb-6">{p.heading}</h2>}
    <div className="grid md:grid-cols-3 gap-5">
      {(p.items || []).map((it, i) => (
        <div key={i} className="p-4 rounded-md border border-border">
          <div className="font-medium text-sm">{it.title}</div>
          <div className="text-xs text-muted-foreground mt-1">{it.body}</div>
        </div>
      ))}
    </div>
  </Section>
);

const PricingRender = ({ props: p }) => (
  <Section tone="muted">
    {p.heading && <h2 className="font-heading text-2xl font-bold mb-6 text-center">{p.heading}</h2>}
    <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
      {(p.plans || []).map((pl, i) => (
        <div key={i} className="p-5 rounded-lg border border-border bg-background text-center">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{pl.name}</div>
          <div className="font-heading text-3xl font-bold mt-2">{pl.price}</div>
          <div className="text-[11px] text-muted-foreground whitespace-pre-line mt-2">{pl.features}</div>
        </div>
      ))}
    </div>
  </Section>
);

const TestimonialsRender = ({ props: p }) => (
  <Section>
    {p.heading && <h2 className="font-heading text-2xl font-bold mb-6">{p.heading}</h2>}
    <div className="grid md:grid-cols-2 gap-4">
      {(p.items || []).map((it, i) => (
        <div key={i} className="p-4 rounded-md border border-border">
          <div className="text-sm italic">“{it.quote}”</div>
          <div className="text-xs text-muted-foreground mt-2">— {it.name}{it.role ? `, ${it.role}` : ""}</div>
        </div>
      ))}
    </div>
  </Section>
);

const FAQRender = ({ props: p }) => (
  <Section tone="muted">
    {p.heading && <h2 className="font-heading text-2xl font-bold mb-5">{p.heading}</h2>}
    <div className="space-y-3">
      {(p.items || []).map((it, i) => (
        <div key={i} className="p-3 rounded-md bg-background border border-border">
          <div className="text-sm font-medium">Q. {it.question}</div>
          <div className="text-xs text-muted-foreground mt-1">{it.answer}</div>
        </div>
      ))}
    </div>
  </Section>
);

const CTARender = ({ props: p }) => (
  <Section tone="primary" className="text-center">
    <h2 className="font-heading text-2xl md:text-3xl font-bold">{p.heading || "Get started"}</h2>
    {p.subheading && <p className="text-sm opacity-90 mt-2">{p.subheading}</p>}
    {p.cta_label && (
      <a href={p.cta_url || "#"} className="inline-block mt-5 px-5 py-2 rounded-full bg-white text-primary text-sm font-medium">
        {p.cta_label}
      </a>
    )}
  </Section>
);

const TextRender = ({ props: p }) => (
  // Sanitise the RichTextEditor's stored HTML before dropping it into the DOM
  // — the editor produces safe markup today, but stored drafts survive across
  // edits and versions, so we defend at render time too.
  <Section><div className="prose prose-sm max-w-none dark:prose-invert"
    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.html || "", { USE_PROFILES: { html: true } }) }} /></Section>
);

const ImageRender = ({ props: p }) => (
  <Section>
    {p.src ? <img src={p.src} alt={p.alt || ""} className="max-w-full rounded-md mx-auto" /> : <div className="h-40 rounded-md border-2 border-dashed border-border grid place-items-center text-xs text-muted-foreground">No image URL</div>}
    {p.caption && <div className="text-xs text-muted-foreground text-center mt-2">{p.caption}</div>}
  </Section>
);

/* ---------------- Block editors ---------------- */
const HeroEditor = ({ props: p, onChange }) => (
  <div className="space-y-3">
    <TextField label="Eyebrow (small label)" value={p.eyebrow} onChange={(v) => onChange({ ...p, eyebrow: v })} />
    <TextField label="Heading" value={p.heading} onChange={(v) => onChange({ ...p, heading: v })} />
    <TextField label="Subheading" textarea value={p.subheading} onChange={(v) => onChange({ ...p, subheading: v })} />
    <div className="grid grid-cols-2 gap-3">
      <TextField label="CTA label" value={p.cta_label} onChange={(v) => onChange({ ...p, cta_label: v })} />
      <TextField label="CTA URL" value={p.cta_url} onChange={(v) => onChange({ ...p, cta_url: v })} />
    </div>
  </div>
);

const FeaturesEditor = ({ props: p, onChange }) => (
  <div className="space-y-3">
    <TextField label="Heading" value={p.heading} onChange={(v) => onChange({ ...p, heading: v })} />
    <ListEditor label="Features" items={p.items}
      onChange={(items) => onChange({ ...p, items })}
      newItem={() => ({ title: "", body: "" })}
      fields={[
        { key: "title", label: "Title" },
        { key: "body",  label: "Body",  textarea: true },
      ]} />
  </div>
);

const PricingEditor = ({ props: p, onChange }) => (
  <div className="space-y-3">
    <TextField label="Heading" value={p.heading} onChange={(v) => onChange({ ...p, heading: v })} />
    <ListEditor label="Plans" items={p.plans}
      onChange={(plans) => onChange({ ...p, plans })}
      newItem={() => ({ name: "", price: "", features: "" })}
      fields={[
        { key: "name",     label: "Name" },
        { key: "price",    label: "Price", placeholder: "₹999 / month" },
        { key: "features", label: "Features (one per line)", textarea: true },
      ]} />
  </div>
);

const TestimonialsEditor = ({ props: p, onChange }) => (
  <div className="space-y-3">
    <TextField label="Heading" value={p.heading} onChange={(v) => onChange({ ...p, heading: v })} />
    <ListEditor label="Testimonials" items={p.items}
      onChange={(items) => onChange({ ...p, items })}
      newItem={() => ({ quote: "", name: "", role: "" })}
      fields={[
        { key: "quote", label: "Quote", textarea: true },
        { key: "name",  label: "Name" },
        { key: "role",  label: "Role" },
      ]} />
  </div>
);

const FAQEditor = ({ props: p, onChange }) => (
  <div className="space-y-3">
    <TextField label="Heading" value={p.heading} onChange={(v) => onChange({ ...p, heading: v })} />
    <ListEditor label="Questions" items={p.items}
      onChange={(items) => onChange({ ...p, items })}
      newItem={() => ({ question: "", answer: "" })}
      fields={[
        { key: "question", label: "Question" },
        { key: "answer",   label: "Answer", textarea: true },
      ]} />
  </div>
);

const CTAEditor = ({ props: p, onChange }) => (
  <div className="space-y-3">
    <TextField label="Heading" value={p.heading} onChange={(v) => onChange({ ...p, heading: v })} />
    <TextField label="Subheading" textarea value={p.subheading} onChange={(v) => onChange({ ...p, subheading: v })} />
    <div className="grid grid-cols-2 gap-3">
      <TextField label="CTA label" value={p.cta_label} onChange={(v) => onChange({ ...p, cta_label: v })} />
      <TextField label="CTA URL" value={p.cta_url} onChange={(v) => onChange({ ...p, cta_url: v })} />
    </div>
  </div>
);

const TextBlockEditor = ({ props: p, onChange }) => (
  <TextField label="HTML" textarea value={p.html} onChange={(v) => onChange({ ...p, html: v })} placeholder="<p>Rich text supported…</p>" />
);

const ImageEditor = ({ props: p, onChange }) => (
  <div className="space-y-3">
    <TextField label="Image URL" value={p.src} onChange={(v) => onChange({ ...p, src: v })} />
    <TextField label="Alt text"  value={p.alt} onChange={(v) => onChange({ ...p, alt: v })} />
    <TextField label="Caption"   value={p.caption} onChange={(v) => onChange({ ...p, caption: v })} />
  </div>
);

/* ---------------- Registry ---------------- */
export const BLOCKS = {
  hero: {
    label: "Hero", category: "landing",
    defaults: () => ({ eyebrow: "", heading: "Big idea in one sentence.", subheading: "", cta_label: "Get started", cta_url: "#" }),
    Editor: HeroEditor, Renderer: HeroRender,
  },
  features: {
    label: "Features", category: "landing",
    defaults: () => ({ heading: "What you get", items: [{ title: "Fast", body: "Blazing quick." }, { title: "Secure", body: "Bank-grade." }, { title: "Simple", body: "No manuals." }] }),
    Editor: FeaturesEditor, Renderer: FeaturesRender,
  },
  pricing: {
    label: "Pricing", category: "landing",
    defaults: () => ({ heading: "Pricing", plans: [{ name: "Starter", price: "Free", features: "Up to 3 users\nBasic reports" }, { name: "Pro", price: "₹999/mo", features: "Unlimited users\nAI features" }] }),
    Editor: PricingEditor, Renderer: PricingRender,
  },
  testimonials: {
    label: "Testimonials", category: "landing",
    defaults: () => ({ heading: "Loved by teams", items: [{ quote: "Changed how we sell.", name: "Rahul", role: "Sales Head" }] }),
    Editor: TestimonialsEditor, Renderer: TestimonialsRender,
  },
  faq: {
    label: "FAQ", category: "landing",
    defaults: () => ({ heading: "Frequently asked", items: [{ question: "How do I start?", answer: "Sign up and follow the setup wizard." }] }),
    Editor: FAQEditor, Renderer: FAQRender,
  },
  cta: {
    label: "Call To Action", category: "landing",
    defaults: () => ({ heading: "Ready to go?", subheading: "", cta_label: "Start free", cta_url: "/signup" }),
    Editor: CTAEditor, Renderer: CTARender,
  },
  text: {
    label: "Rich Text", category: "content",
    defaults: () => ({ html: "<p>Write anything…</p>" }),
    Editor: TextBlockEditor, Renderer: TextRender,
  },
  image: {
    label: "Image", category: "content",
    defaults: () => ({ src: "", alt: "", caption: "" }),
    Editor: ImageEditor, Renderer: ImageRender,
  },
};

export function getBlock(type) {
  return BLOCKS[type] || null;
}

export function makeBlock(type) {
  const b = BLOCKS[type];
  return { id: `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`, type, props: b ? b.defaults() : {} };
}
